import { apiFetch, toArray } from '@/services/core/apiFetch'
import { throwApiError } from '@/services/core/apiError'
import { getContext } from '@/services/networks/networksApi'
import { searchParams } from '@/services/core/listSearch'
import { mapValidationWarnings, type ValidationWarning } from '@/services/core/validationWarnings'
import type { Fee } from '@/services/earnings/fees'
import type { Targeting } from '@/services/targeting/targeting'
import type { Network } from '@/services/networks/networksApi'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`
const JSON_HEADERS = { 'Content-Type': 'application/json' }

/** Kinds of team a network can have — mirrors `Team.Type` in core. */
export type TeamType = 'STANDARD' | 'AX_ADVERTISER' | 'AX_PUBLISHER'

/** A creative format (layout) fee on a marketplace advertiser team. */
export interface TeamLayoutFee {
  layout: { id: string; name?: string }
  fee: Fee
}

export interface Team {
  id: string
  name: string
  type?: string
  defaultCurrency?: string
  description?: string
  externalReference?: string
  country?: string
  labels?: string[]
  /** Targeting every line item of a marketplace advertiser team starts with. */
  defaultTargeting?: Targeting
  techFeeCpm?: Fee | null
  creativeTagTechFeeCpm?: Fee | null
  supplementaryFees?: Fee[]
  /** Keyed by the targeting type's API name, e.g. `SEMANTIC_CATEGORY`. */
  targetingTypeTechFees?: Record<string, Fee>
  layoutFees?: TeamLayoutFee[]
  objectState?: string
  createTime?: string
  updateTime?: string
  /** Problems the server found on the last save; only set on a full fetch. */
  validationWarnings?: ValidationWarning[]
}

export interface TeamInput {
  name: string
  type?: string
  defaultCurrency?: string
  objectState?: string
  description?: string
  externalReference?: string
  country?: string | null
  labels?: string[]
  defaultTargeting?: Targeting
  techFeeCpm?: Fee | null
  creativeTagTechFeeCpm?: Fee | null
  supplementaryFees?: Fee[]
  targetingTypeTechFees?: Record<string, Fee>
  layoutFees?: TeamLayoutFee[]
}

interface ApiTeam {
  id: string
  name: string
  type?: string
  defaultCurrency?: string
  description?: string
  externalReference?: string
  country?: string
  labels?: string[]
  defaultTargeting?: Targeting
  techFeeCpm?: Fee | null
  creativeTagTechFeeCpm?: Fee | null
  supplementaryFees?: Fee[]
  targetingTypeTechFees?: Record<string, Fee>
  layoutFees?: TeamLayoutFee[]
  objectState?: string
  createTime?: string
  updateTime?: string
  validationWarnings?: unknown
}

function mapTeam(r: ApiTeam): Team {
  return {
    id: r.id ?? '',
    name: r.name ?? '',
    type: r.type,
    defaultCurrency: r.defaultCurrency,
    description: r.description,
    externalReference: r.externalReference,
    country: r.country,
    labels: r.labels,
    defaultTargeting: r.defaultTargeting,
    techFeeCpm: r.techFeeCpm,
    creativeTagTechFeeCpm: r.creativeTagTechFeeCpm,
    supplementaryFees: r.supplementaryFees,
    targetingTypeTechFees: r.targetingTypeTechFees,
    layoutFees: r.layoutFees,
    objectState: r.objectState,
    createTime: r.createTime,
    updateTime: r.updateTime,
    validationWarnings: mapValidationWarnings(r.validationWarnings),
  }
}

export const TeamsApi = {
  getAll: async (search?: string): Promise<Team[]> => {
    const params = new URLSearchParams({
      ...searchParams('teams', search),
      context: getContext(),
      includeActive: 'true',
      includeInactive: 'false',
      orderBy: 'name',
      pageSize: '1001',
    })
    const res = await apiFetch(`${API_BASE}/teams?${params}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) throw new Error(`Failed to fetch teams (${res.status})`)
    const data = await res.json()
    const results = toArray<ApiTeam>(data)
    return results.map(mapTeam)
  },

  getById: async (id: string): Promise<Team> => {
    const res = await apiFetch(`${API_BASE}/teams/${id}?context=${getContext()}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) await throwApiError(res, 'Failed to fetch team')
    return mapTeam(await res.json())
  },

  create: async (id: string, input: TeamInput): Promise<Team> => {
    const res = await apiFetch(`${API_BASE}/teams/${id}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ id, ...input }),
    })
    if (!res.ok) await throwApiError(res, 'Failed to create team')
    return mapTeam(await res.json())
  },

  update: async (id: string, input: TeamInput): Promise<Team> => {
    const res = await apiFetch(`${API_BASE}/teams/${id}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(input),
    })
    if (!res.ok) await throwApiError(res, 'Failed to save team')
    return mapTeam(await res.json())
  },

  /** The active teams of one kind, e.g. the marketplace advertiser teams. */
  getByType: async (type: TeamType): Promise<Team[]> => {
    const params = new URLSearchParams({
      context: getContext(),
      filterBy: 'name;description',
      filterByLike: '',
      includeActive: 'true',
      includeHidden: 'false',
      includeInactive: 'false',
      minimal: 'true',
      orderBy: 'name',
      pageSize: '1001',
    })
    const res = await apiFetch(`${API_BASE}/teams?${params}&where=${encodeURIComponent(`type = ${type}`)}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) throw new Error(`Failed to fetch ${type} teams (${res.status})`)
    const data = await res.json()
    const results = toArray<ApiTeam>(data)
    return results.map(mapTeam)
  },

  getAdvertiserTeams: (): Promise<Team[]> => TeamsApi.getByType('AX_ADVERTISER'),
}

/** The team types a network's platforms allow — as the old UI offers. */
export function availableTeamTypes(network: Network | null): string[] {
  let types: string[] = []
  if (network?.suiPlatform) {
    types = ['STANDARD', 'SUI', 'AX_ADVERTISER', 'AX_PUBLISHER']
  } else if (network?.advertisingPlatform) {
    types = ['STANDARD']
    if (network.marketplacePlatform) types.push('AX_ADVERTISER', 'AX_PUBLISHER', 'CREATIVE_AGENCY')
  }
  if (network?.politicalApp) {
    for (const type of ['CONTROLLER', 'SPONSOR']) if (!types.includes(type)) types.push(type)
  }
  return types
}
